import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import LogoutButton from "./LogoutButton";
import "./Dashboard.css";

import { LoginContext } from "../context/LoginContext";
import { apiFetch } from "../apis/apiFetch";
import apiConfig from "../config/apiConfig";
import { toast } from "../utils/toast";
import { useWithdrawalsViewModel } from "../viewModels/useWithdrawalsViewModel";

export default function WithdrawApplication() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { user } = useContext(LoginContext);

    const [showDropdown, setShowDropdown] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [errors, setErrors] = useState<Record<string, string>>({});

    // const [dataToSave, setDataToSave] = useState<any>({
    //     address: "",
    //     reason: "",
    //     reason_desc: "",
    //     bank_acc_no: "",
    //     bank_acc_name: "",
    //     bank_address: "",
    //     bank_ifsc: "",
    //     confirm: false,
    // });

    const reasons = [
        "Employment",
        "Health",
        "Admission elsewhere",
        "Other",
    ];

    const { data: dataRes, isFetching } = useQuery({
        queryKey: ["withdrawApplication", user?.userId],

        queryFn: async () => {
            if (!user?.userId) return [];

            const response = await apiFetch(
                `${apiConfig.getResourceUrl(
                    "applications"
                )}?queryId=GET_APPLICATIONS_BY_USER_ID&args=user_id:${user.userId
                }`,
                {
                    method: "GET",
                }
            );

            if (!response.ok) {
                throw new Error("Unable to fetch application");
            }

            const data = await response.json();

            return data.resource || [];
        },

        enabled: !!user?.userId,
    });

    useEffect(() => {
        if (!isFetching && dataRes?.length) {
            const application = dataRes.find(
                (r: any) =>
                    r.user_id === user?.userId
            );

            if (application) {
                // Determine the programme the candidate currently holds by checking all possible slots
                const allSlots = [
                    { p: application.online_round5_allocated_programme_2, s: application.online_round5_offer_status_2 },
                    { p: application.online_round5_allocated_programme, s: application.online_round5_offer_status },
                    { p: application.online_round4_allocated_programme_2, s: application.online_round4_offer_status_2 },
                    { p: application.online_round4_allocated_programme, s: application.online_round4_offer_status },
                    { p: application.online_round3_allocated_programme_2, s: application.online_round3_offer_status_2 },
                    { p: application.online_round3_allocated_programme, s: application.online_round3_offer_status },
                    { p: application.online_round2_allocated_programme_2, s: application.online_round2_offer_status_2 },
                    { p: application.online_round2_allocated_programme, s: application.online_round2_offer_status },
                    { p: application.online_round_allocated_programme_2, s: application.online_offer_status_2 },
                    { p: application.online_round_allocated_programme, s: application.online_offer_status },
                    { p: application.offered_programme_3, s: application.offer_status_3 },
                    { p: application.offered_programme_2, s: application.offer_status_2 },
                    { p: application.offered_programme_1, s: application.offer_status_1 },
                ];

                // Prioritize the most recent round (which appears first in allSlots) that has a valid active status
                const activeSlot = 
                    allSlots.find((slot) => slot.p && ["OFFER_SENT", "ACCEPTED", "OFFER_CONFIRMED"].includes(slot.s));

                let currentProgramme = 
                    activeSlot?.p || 
                    application.offered_programme || 
                    application.offered_payment_programme;

                setDataToSave((prev: any) => ({
                    ...prev,

                    id: application.id,
                    app_id: application.application_number,

                    name: application.fullname,
                    year: "2026",
                    programme: currentProgramme,
                    email: application.email,
                    contact: application.guardian_mobile,
                    rollNo: application.rollNo,
                    dob: application.dob,
                    status: "INITIATED",
                    address: `${application?.p_line1}, ${application?.p_line2}, ${application?.p_pincode}, ${application?.p_city}, ${application?.p_country}.`
                }));
            }
        }
    }, [dataRes, isFetching]);

    const handleChange = (
        field: string,
        value: string | boolean
    ) => {
        setDataToSave((prev: any) => ({
            ...prev,
            [field]: value,
        }));

        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: "",
            }));
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        // if (!dataToSave.address?.trim())
        //     newErrors.address = "Address is required.";

        if (!dataToSave.reason)
            newErrors.reason = "Reason is required.";

        if (
            dataToSave.reason === "Other" &&
            !dataToSave.reason_desc?.trim()
        ) {
            newErrors.reason_desc =
                "Please specify your reason.";
        }

        if (!dataToSave.bank_acc_no?.trim()) {
            newErrors.bank_acc_no =
                "Account Number is required.";
        }
        // else if (
        //     !/^[0-9]{9,18}$/.test(dataToSave.bank_acc_no)
        // ) {
        //     newErrors.bank_acc_no =
        //         "Invalid Account Number.";
        // }

        if (!dataToSave.bank_acc_name?.trim()) {
            newErrors.bank_acc_name =
                "Account Holder Name is required.";
        }

        if (!dataToSave.bank_address?.trim()) {
            newErrors.bank_address =
                "Bank Address is required.";
        }

        if (!dataToSave.bank_ifsc?.trim()) {
            newErrors.bank_ifsc =
                "IFSC Code is required.";
        }
        // else if (
        //     !/^[A-Z]{4}0[A-Z0-9]{6}$/.test(
        //         dataToSave.bank_ifsc
        //     )
        // ) {
        //     newErrors.bank_ifsc =
        //         "Invalid IFSC Code.";
        // }

        if (!dataToSave.confirm) {
            newErrors.confirm =
                "Please confirm before submitting.";
        }

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (!validateForm()) {
            toast.error("Please fill all mandatory fields.");
            return;
        }

        setShowConfirmModal(true);
    };

    const {
        dataToSave,
        setDataToSave,
        save
    } = useWithdrawalsViewModel(user?.userId || "");

    const executeSave = async () => {
        try {
            setIsSaving(true);
            await save()
            toast.success("Withdrawal request submitted.");

            navigate("/applicant/home");
        } catch (err) {
            console.error(err);
            toast.error("Something went wrong.");
        } finally {
            setIsSaving(false);
            setShowConfirmModal(false);
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>

            {/* Header */}

            <header className="dash-header">
                <h1 className="dash-page-title">
                    Withdrawal Application
                </h1>

                <div className="dash-user-profile">
                    <div
                        className="dash-profile-circle"
                        onClick={() => setShowDropdown((prev) => !prev)}
                    >
                        <span>S</span>
                    </div>

                    {showDropdown && (
                        <div className="dash-profile-dropdown">
                            <LogoutButton />
                        </div>
                    )}
                </div>
            </header>

            {(isFetching || isSaving) ? (
                <div>Loading...</div>
            ) : (

                <div className="dash-content-body">

                    <div className="form-container">

                        {/* =======================
                        Withdrawal Details
                    ======================= */}

                        <div className="form-container">

                            <h3 className="form-subtitle">
                                Withdrawal Details
                            </h3>

                            <div className="form-grid">

                                <div className="form-group">
                                    <label className="form-label">
                                        Application ID
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.app_id || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Candidate Name
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.name || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Programme
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.programme || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Admission Year
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.year || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Email
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.email || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Contact Number
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.contact || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Roll Number
                                    </label>

                                    <input
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.rollNo || ""}
                                        disabled
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Date of Birth
                                    </label>

                                    <input
                                        type="date"
                                        className="form-input"
                                        style={{ color: "black" }}
                                        value={dataToSave.dob || ""}
                                        disabled
                                    />
                                </div>

                                <div
                                    className="form-group"
                                    style={{ gridColumn: "1 / -1" }}
                                >
                                    <label className="form-label">
                                        Address *
                                    </label>

                                    <textarea
                                        className={`form-input ${errors?.address ? "input-error" : ""}`}
                                        style={{ color: "black" }}
                                        rows={3}
                                        value={dataToSave?.address || ""}
                                        onChange={(e) =>
                                            handleChange(
                                                "address",
                                                e.target.value
                                            )
                                        }
                                        disabled
                                    />

                                    {errors.address && (
                                        <span className="error-text">
                                            {errors.address}
                                        </span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Reason for Withdrawal *
                                    </label>

                                    <select
                                        className={`form-input ${errors.reason ? "input-error" : ""}`}
                                        style={{ color: "black" }}
                                        value={dataToSave.reason}
                                        onChange={(e) =>
                                            handleChange(
                                                "reason",
                                                e.target.value
                                            )
                                        }
                                    >
                                        <option value="">
                                            Select Reason
                                        </option>

                                        {reasons.map((reason) => (
                                            <option
                                                key={reason}
                                                value={reason}
                                            >
                                                {reason}
                                            </option>
                                        ))}
                                    </select>

                                    {errors.reason && (
                                        <span className="error-text">
                                            {errors.reason}
                                        </span>
                                    )}
                                </div>

                                {dataToSave.reason === "Other" && (
                                    <div
                                        className="form-group"
                                        style={{ gridColumn: "1 / -1" }}
                                    >
                                        <label className="form-label">
                                            Please Specify *
                                        </label>

                                        <textarea
                                            rows={4}
                                            className={`form-input ${errors.reason_desc ? "input-error" : ""}`}
                                            value={dataToSave.reason_desc}
                                            onChange={(e) =>
                                                handleChange(
                                                    "reason_desc",
                                                    e.target.value
                                                )
                                            }
                                        />

                                        {errors.reason_desc && (
                                            <span className="error-text">
                                                {errors.reason_desc}
                                            </span>
                                        )}
                                    </div>
                                )}

                            </div>
                        </div>

                        {/* =======================
                        Bank Details
                    ======================= */}

                        <div
                            className="form-container"
                            style={{ marginTop: "30px" }}
                        >

                            <h3 className="form-subtitle">
                                Bank Details
                            </h3>

                            <div className="form-grid">

                                <div className="form-group">
                                    <label className="form-label">
                                        Bank Account Number *
                                    </label>

                                    <input
                                        className={`form-input ${errors.bank_acc_no ? "input-error" : ""}`}
                                        value={dataToSave.bank_acc_no}
                                        onChange={(e) =>
                                            handleChange(
                                                "bank_acc_no",
                                                e.target.value
                                            )
                                        }
                                    />

                                    {errors.bank_acc_no && (
                                        <span className="error-text">
                                            {errors.bank_acc_no}
                                        </span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Name as in bank A/C *
                                    </label>

                                    <input
                                        className={`form-input ${errors.bank_acc_name ? "input-error" : ""}`}
                                        value={dataToSave.bank_acc_name}
                                        onChange={(e) =>
                                            handleChange(
                                                "bank_acc_name",
                                                e.target.value
                                            )
                                        }
                                    />

                                    {errors.bank_acc_name && (
                                        <span className="error-text">
                                            {errors.bank_acc_name}
                                        </span>
                                    )}
                                </div>

                                <div
                                    className="form-group"
                                    style={{ gridColumn: "1 / -1" }}
                                >
                                    <label className="form-label">
                                        Name and full address code *
                                    </label>

                                    <textarea
                                        rows={3}
                                        className={`form-input ${errors.bank_address ? "input-error" : ""}`}
                                        value={dataToSave.bank_address}
                                        onChange={(e) =>
                                            handleChange(
                                                "bank_address",
                                                e.target.value
                                            )
                                        }
                                    />

                                    {errors.bank_address && (
                                        <span className="error-text">
                                            {errors.bank_address}
                                        </span>
                                    )}
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        IFSC code of bank branch *
                                    </label>

                                    <input
                                        style={{ textTransform: "uppercase" }}
                                        className={`form-input ${errors.bank_ifsc ? "input-error" : ""}`}
                                        value={dataToSave.bank_ifsc}
                                        onChange={(e) =>
                                            handleChange(
                                                "bank_ifsc",
                                                e.target.value.toUpperCase()
                                            )
                                        }
                                    />

                                    {errors.bank_ifsc && (
                                        <span className="error-text">
                                            {errors.bank_ifsc}
                                        </span>
                                    )}
                                </div>

                            </div>
                        </div>
                        {/* =======================
                        Declaration
                    ======================= */}

                        <div
                            className="form-container"
                            style={{ marginTop: "30px", display: "flex", gap: 10 }}
                        >
                            <div>

                                <input
                                    type="checkbox"
                                    checked={dataToSave.confirm}
                                    onChange={(e) =>
                                        handleChange(
                                            "confirm",
                                            e.target.checked
                                        )
                                    }
                                    style={{
                                        marginBottom: "10px",
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start",
                                    gap: "10px",
                                    marginTop: 20
                                }}
                            >
                                <label
                                    style={{
                                        lineHeight: "1.6",
                                        fontSize: "14px",
                                        // marginBottom: "20px"
                                    }}
                                >
                                    * I note that all bank charges will be borne by the beneficiary.
                                </label>


                                <label
                                    style={{
                                        lineHeight: "1.6",
                                        fontSize: "14px",
                                    }}
                                >
                                    * I wish to apply herewith for the refundable portion of the paid tuition fee as I will not commence my
                                    studies with the aforementioned programme at the date stated in my offer letter. I have stated my
                                    reasons above and confirm herewith that the information given in this document is to the best of my
                                    knowledge, true and correct.
                                </label>
                            </div>

                            {errors.confirm && (
                                <span
                                    className="error-text"
                                    style={{ marginTop: "8px" }}
                                >
                                    {errors.confirm}
                                </span>
                            )}
                        </div>

                    </div>

                    {/* =======================
                    Submit Button
                ======================= */}

                    <div className="form-actions">
                        <button
                            className="save-btn"
                            onClick={handleSubmit}
                            disabled={isSaving}
                            style={{
                                background: isSaving || !dataToSave.confirm ? "#d27c85" : "#dc3545",
                                cursor: isSaving || !dataToSave.confirm ? "default" : "pointer",
                            }}
                        >
                            {isSaving
                                ? "Submitting..."
                                : "Submit Withdrawal Request"}
                        </button>
                    </div>

                </div>
            )}

            {/* =======================
            Confirmation Modal
        ======================= */}

            {showConfirmModal && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        background: "rgba(0,0,0,0.45)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                        backdropFilter: "blur(3px)",
                    }}
                >
                    <div
                        style={{
                            background: "#fff",
                            borderRadius: "12px",
                            width: "450px",
                            maxWidth: "90%",
                            padding: "30px",
                        }}
                    >
                        <h3
                            style={{
                                marginBottom: "15px",
                            }}
                        >
                            Confirm Submission
                        </h3>

                        <p
                            style={{
                                color: "#555",
                                lineHeight: "1.6",
                                marginBottom: "25px",
                            }}
                        >
                            Are you sure you want to submit your withdrawal
                            application?
                            <br />
                            <br />
                            Once submitted, you will not be able to modify or undo.
                        </p>

                        <div style={{ display: 'flex', gap: "10px" }}>
                            <input
                                type="checkbox"
                                checked={dataToSave.check}
                                onChange={(e) =>
                                    handleChange(
                                        "check",
                                        e.target.checked
                                    )
                                }
                                style={{
                                    marginTop: "4px",
                                }}
                            />
                            <p
                                style={{
                                    color: "#555",
                                    lineHeight: "1.6",
                                    marginBottom: "25px",
                                }}
                            >
                                I confirm submission
                            </p>
                        </div>

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: "12px",
                            }}
                        >
                            <button
                                onClick={() =>
                                    setShowConfirmModal(false)
                                }
                                style={{
                                    padding: "10px 20px",
                                    border: "1px solid #ccc",
                                    background: "#fff",
                                    borderRadius: "6px",
                                    cursor: "pointer",
                                }}
                            >
                                Cancel
                            </button>

                            <button
                                onClick={executeSave}
                                disabled={isSaving || !dataToSave.check}
                                style={{
                                    padding: "10px 20px",
                                    border: "none",
                                    background: isSaving || !dataToSave.check ? "#d27c85" : "#dc3545",
                                    color: "#fff",
                                    borderRadius: "6px",
                                    cursor: isSaving || !dataToSave.check ? "default" : "pointer",
                                }}
                            >
                                {isSaving
                                    ? "Submitting..."
                                    : "Yes, Submit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}